import React, { useMemo, useState } from 'react';
import { Box, Grid, IconButton, InputLabel, Typography } from '@mui/material';
import { ArrowBack, Close, EventAvailableOutlined } from '@mui/icons-material';
import { DateFormats, TimeFormats } from '@uplift/constants';
import { L10nHTML, useL10n } from '@uplift/hooks';
import { Spinner, Alert, Button, SubmitButton, TextInput } from '@uplift/ui-v2';
import { Form, Formik } from 'formik';
import { AppointmentSlotDuration, createPsychiatryAppointment } from '@uplift/api';
import { usePsychiatryMatchingContext } from './psychiatry-matching-context';
import { useStyles } from './styles';
import { CalendarDateService } from '@uplift/services';
import { useQueryClient } from '@tanstack/react-query';
import { useAllTimezonesQuery, useUserProfileQuery } from '@uplift/queries';


type ConfirmSessionForm = {
  notes?: string;
};


export const ConfirmSession: React.VFC = () => {
  const reactQueryClient = useQueryClient();
  const { state, dispatch } = usePsychiatryMatchingContext();
  const { classes } = useStyles({ isDrawer: state.isDrawer });
  const l10n = useL10n();
  const [ errorAlertMessage, setErrorAlertMessage ] = useState<string | null>(null);
  const selectedProvider = state.providers.find(x => x.user.id === state.selectedProvider);
  const userName = selectedProvider.nameDisplay;
  const fullWithAffixes = userName?.fullWithAffixes;
  const subtitleName = useMemo(() => {
    if (userName?.prefix === 'Dr.') {
      return `${userName.prefix} ${userName.last}`;
    }
    return `${userName.first} ${userName.last}`;
  }, [ userName ]);
  const { data: patientProfile, isLoading: isPatientProfileLoading } = useUserProfileQuery(state.patientId);
  const { data: timezones = [] } = useAllTimezonesQuery();
  const timezone = timezones?.find(x => x.ianaName === patientProfile?.timezone);
  const initialValues: ConfirmSessionForm = {
    notes: ''
  };

  if (isPatientProfileLoading) {
    return (
      <Box className={classes.halfWidth}>
        <Spinner />
      </Box>
    );
  }

  const selectedDate = CalendarDateService.date.utc(state.appointment.fromDatetime).tz(timezone?.ianaName);
  const dateText = selectedDate.format(DateFormats['dddd, MMM. D']);
  const from = selectedDate.format(selectedDate.minute() === 0 ? TimeFormats['h'] : TimeFormats['h:mm']);
  const toObj = CalendarDateService.date(selectedDate).add(1, 'hour');
  const to = toObj.format(toObj.minute() === 0 ? TimeFormats['ha'] : TimeFormats['h:mma']);

  const handleSubmit = async (values: ConfirmSessionForm) => {
    try {
      const result = await createPsychiatryAppointment({
        provider_id: state.selectedProvider,
        patient_id: state.patientId,
        notes: values.notes,
        time_start: selectedDate.format(),
        duration: AppointmentSlotDuration.sixty,
        timezone: timezone?.ianaName,
        source: state.source,
      });

      if (result?.error) {
        const errorMessage = l10n.schedule.createAppointment.referralErrors[result.error];

        if (errorMessage) {
          setErrorAlertMessage(errorMessage.body);
        } else if (result.error) {
          setErrorAlertMessage(l10n.psychiatry.confirm.unknownError);
        }

        throw new Error(result.error);
      }

      // TODO: extract `createPsychiatryAppointment` to mutation hook with invalidation in `onSuccess`
      await Promise.all([
        reactQueryClient.invalidateQueries({ queryKey: [ 'user', state.patientId, 'matches' ] }),
        reactQueryClient.invalidateQueries({ queryKey: [ 'user', state.patientId, 'activity' ] }),
      ]);

      dispatch({
        type: 'finish',
        payload: true
      });

      state.onDone(true);
    } catch (err) {
      console.error(err);
    }
  };

  const handleBack = () => {
    dispatch({
      type: 'changeStep',
      payload: 'profile',
    });
  };

  return (
    <>
      {state?.isDrawer && (
        <Box p={1}>
          <Button startIcon={<ArrowBack fontSize="inherit" />} type="button" color="secondary" size="small" onClick={handleBack} className={classes.backButton}>
            {l10n.navigation.back}
          </Button>
        </Box>
      )}
      <Box className={classes.halfWidth}>
        <Typography variant={state?.isDrawer ? 'h4' : 'h2'}>
          {l10n.psychiatry.confirm.confirm(state.patientName)}
        </Typography>
        <Box my={3}>
          {errorAlertMessage && (
            <Box mb={3}>
              <Alert
                variant="filled"
                severity="error"
                action={(
                  <IconButton
                    aria-label="close"
                    color="inherit"
                    size="small"
                    onClick={() => {
                      setErrorAlertMessage(null);
                    }}
                  >
                    <Close fontSize="inherit" />
                  </IconButton>
                )}
              >
                <L10nHTML>
                  {errorAlertMessage}
                </L10nHTML>
              </Alert>
            </Box>
          )}
          <Grid container>
            <Grid size={1}>
              <EventAvailableOutlined className={classes.icon} />
            </Grid>
            <Grid>
              <Typography variant="h5">
                {dateText} at {from} &ndash; {to} {timezone.shortName}
              </Typography>
              {l10n.psychiatry.confirm.with} {fullWithAffixes}
            </Grid>
          </Grid>
        </Box>
        <Formik initialValues={initialValues} onSubmit={handleSubmit}>
          <Form>
            <InputLabel htmlFor="notes">
              {l10n.psychiatry.confirm.informationRequired(subtitleName)}
            </InputLabel>
            <TextInput
              multiline
              fullWidth
              name="notes"
              rows={5}
            />
            <Box mt={5}>
              <SubmitButton type="submit" variant="contained" color="secondary" fullWidth>
                {l10n.psychiatry.confirm.book}
              </SubmitButton>
            </Box>
            <br />
            <Typography fontStyle="italic" fontSize=".75em">{l10n.findPsychiatrist.noCSWarning}</Typography>
          </Form>
        </Formik>
      </Box>
    </>
  );
};
